import { ReactElement, useMemo } from 'react';
import storeConnector from '@store/storeConnector';
import useGetInternalWidgetConfig from '@src/hooks/api/queries/useGetInternalWidgetConfig';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import useSubscription from '@src/hooks/useSubscription';
import TopSwitchResistorSection from './components/TopSwitchResistorSection';
import BreakerStack from './components/BreakerStack';
import MultiSwitchConnector from '../components/MultiSwitchConnector/MultiSwitchConnector';
import {
  getUnitControllerDataPointsWithValues,
  UnitController,
} from './devicePointsConfig';
import C_Units from './components/C_Units';
import A_Units from './components/A_Units';
import D_Units from './components/D_Units';
import B_Units from './components/B_Units';
import Header from './components/Header';

function CanyonCountry_Diagram(): ReactElement {
  let unitControllersWithDataPointValues: UnitController[] = [];

  const { data: unitWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'canyon_country_sld_unit',
      {},
      { enabled: true },
    );

  const { data: rtacWidgetConfig } =
    useGetInternalWidgetConfig<WidgetTypeDeviceList>(
      'canyon_country_sld_rtac',
      {},
      { enabled: true },
    );

  const dataPointsToSubscribe = useMemo(
    () => [
      ...(unitWidgetConfig?.dataPoints || []),
      ...(rtacWidgetConfig?.dataPoints || []),
    ],
    [unitWidgetConfig, rtacWidgetConfig],
  );

  const dataPointsWithValues = useSubscription(dataPointsToSubscribe);

  if (dataPointsWithValues.length > 0) {
    unitControllersWithDataPointValues =
      getUnitControllerDataPointsWithValues(dataPointsWithValues);
  }

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      xlinkHref='http://www.w3.org/1999/xlink'
      viewBox='200 40 1200 700'
      xmlSpace='preserve'
      colorInterpolationFilters='sRGB'
      className='st10'
      style={{ fill: 'none', stroke: 'currentColor', overflowX: 'auto' }}
    >
      <title>Canyon Country SLD</title>
      <Header />

      <TopSwitchResistorSection
        translateXCoordinate={75}
        translateYCoordinate={75}
        rtacDataPointsWithValues={dataPointsWithValues}
      />

      <MultiSwitchConnector
        translateXCoordinate={312.5}
        translateYCoordinate={-295}
        verticalLength={32}
        horizontalLength={120}
        startingXCoordinate={410}
      />

      <MultiSwitchConnector
        translateXCoordinate={285}
        translateYCoordinate={-215}
        verticalLength={30}
        horizontalLength={50}
        startingXCoordinate={410}
      />

      <MultiSwitchConnector
        translateXCoordinate={390}
        translateYCoordinate={-215}
        verticalLength={31}
        horizontalLength={80}
        startingXCoordinate={410}
      />

      <BreakerStack
        translateXCoordinate={835}
        translateYCoordinate={-430}
        breakerLabel='52F2'
        rtacDataPointsWithValues={dataPointsWithValues}
        rtacPointName='PCB_52F2_Status'
      />

      <BreakerStack
        translateXCoordinate={955}
        translateYCoordinate={-430}
        breakerLabel='52F1'
        rtacDataPointsWithValues={dataPointsWithValues}
        rtacPointName='PCB_52F1_Status'
      />

      {/* F2 C Units */}
      {/*  1st number is horizontal , 2nd number is vertical */}
      <g transform='translate(-90 220)'>
        <C_Units
          unitControllersWithDataPointValues={
            unitControllersWithDataPointValues
          }
        />
      </g>

      {/* F1 A Units */}
      {/*  1st number is horizontal , 2nd number is vertical */}
      <g transform='translate(550 220)'>
        <A_Units
          unitControllersWithDataPointValues={
            unitControllersWithDataPointValues
          }
        />
      </g>

      {/* F2 D Units */}
      {/*  1st number is horizontal , 2nd number is vertical */}
      <g transform='translate(-70 350)'>
        <D_Units
          unitControllersWithDataPointValues={
            unitControllersWithDataPointValues
          }
        />
      </g>

      {/* F1 B Units */}
      {/*  1st number is horizontal , 2nd number is vertical */}
      <g transform='translate(490 350)'>
        <B_Units
          unitControllersWithDataPointValues={
            unitControllersWithDataPointValues
          }
        />
      </g>
    </svg>
  );
}

export default storeConnector(CanyonCountry_Diagram, {
  config: ['siteMeta'],
});
